<template>
  <div>
    <a-article
      v-if="articleData"
      :article-data="articleData"
      :related-articles="relatedArticles"
      :related-videos="relatedVideos"
      :article-slug="articleSlug"
      :category-slug="categorySlug"
      :subcategory-slug="subcategorySlug"
      :is-ebook-loaded="isEbookLoaded"
      :show-disclaimer="isDisclaimerVisible"
      @get-ebook-data="getEbookData"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import AArticle from '@/components/_sections/article/AArticle'
import { generateArticlePathBySlugs } from '@/plugins/helper'
import { EBOOK_LOCATION } from 'shared/AEbook/enums'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { throwPageNotFoundError } from '@/plugins/error'
import { checkIfNonCanonicalPath } from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { pick } from 'ramda'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'

async function getArticlePageData({ store, route }) {
  const {
    query: { previewId }
  } = route
  const { category, subcategory, article: articleSlug } = route.params

  const { dispatch } = store

  const payload = {
    articleSlug: articleSlug.toLowerCase(),
    previewId
  }

  const { articleData, relatedArticles, relatedVideos } = await dispatch(
    'articles/requestArticlePageData',
    payload
  )

  const ebookFetchProps = {
    location: EBOOK_LOCATION.ARTICLE,
    requestPayload: payload
  }

  return {
    articleData,
    relatedArticles,
    relatedVideos,
    articleSlug,
    categorySlug: category,
    subcategorySlug: subcategory,
    ebookFetchProps
  }
}

function generateDefaultArticleContentDataModel() {
  return {
    articleData: null,
    relatedArticles: null,
    relatedVideos: []
  }
}

export default {
  name: 'ArticlePage',
  components: { AArticle },
  mixins: [
    mixins.runMethodOnLoginLogout,
    mixins.refreshDataHandler,
    mixins.setPageLoaded,
    mixins.ebookDataFetch,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE,
        [PENDING_ACTION.FOLLOW_AUTHOR]:
          METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const {
      query: { previewId }
    } = ctx.route
    const { subcategory, article: articleSlug } = ctx.route.params

    try {
      const dispatch = ctx.store.dispatch

      if (!articleSlug && !previewId) return {}

      try {
        const pageData = await getArticlePageData({
          store: ctx.store,
          route: ctx.route
        })

        /**
         * This check covers the case with ROUTE_NAME = CATEGORY_SUBCATEGORY_ARTICLE_GIBBERISH
         * because canonicalURL will never be the same as URL with gibberish.
         */
        if (
          !previewId &&
          checkIfNonCanonicalPath(ctx, {
            currentPath,
            canonicalUrl: pageData.articleData.canonicalUrl
          })
        ) {
          redirectHandler({ ctx, to: pageData.articleData.canonicalUrl })
          return
        }

        return pageData
      } catch (err) {
        const { categorySlug } = await dispatch(
          'articles/requestArticleContentBySlug',
          {
            articleSlug: subcategory,
            previewId
          }
        )

        const redirectPath = generateArticlePathBySlugs({
          articleSlug: subcategory,
          categorySlug,
          subcategorySlug: null
        })

        redirectHandler({ ctx, to: redirectPath })
      }
    } catch (e) {
      throwPageNotFoundError(ctx, 'ArticlePage')
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      ...generateDefaultArticleContentDataModel(),
      articleSlug: null,
      categorySlug: null,
      subcategorySlug: null,
      isEbookLoaded: false
    }
  },
  computed: {
    ...mapGetters({
      checkIfThoughtLeadershipSlug: 'categories/checkIfThoughtLeadershipSlug'
    }),
    isDisclaimerVisible() {
      return this.checkIfThoughtLeadershipSlug(this.categorySlug)
    }
  },
  methods: {
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE]({ id, value }) {
      if (this.articleData.mainTag.id !== id) return

      this.articleData.mainTag = {
        ...this.articleData.mainTag,
        isFollowed: value
      }
      this.articleData.tags = this.articleData.tags.map(tagData => {
        if (tagData.id === id) return this.articleData.mainTag

        return tagData
      })
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE]({ id, value }) {
      this.articleData.publishers = this.articleData.publishers.map(
        publisher => {
          if (publisher.id === id) {
            return {
              ...publisher,
              isFollowed: value
            }
          }

          return publisher
        }
      )
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE]({ value }) {
      this.articleData.isSaved = value
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articleData.isSaved = false
      this.articleData.tags = this.articleData.tags.map(tag => ({
        ...tag,
        isFollowed: false
      }))
      this.articleData.publishers = this.articleData.publishers.map(
        publisher => ({ ...publisher, isFollowed: false })
      )
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const payload = {
        articleSlug: this.$helper.getLastPathParam(this.$route.path)
      }

      const {
        isSaved = this.articleData.isSaved,
        authorsIsFollowed = this.articleData.publishers.map(
          ({ isFollowed }) => isFollowed
        ),
        tags = this.articleData.tags,
        mainTag = this.articleData.mainTag
      } = await this.$_refreshDataHandler_getSingleArticleEntities(payload)

      this.articleData = {
        ...this.articleData,
        isSaved,
        publishers: this.articleData.publishers.map((publisher, index) => ({
          ...publisher,
          isFollowed: authorsIsFollowed[index]
        })),
        tags,
        mainTag
      }
    },
    async getEbookData() {
      const { location, requestPayload } = this.ebookFetchProps || {}

      this.articleData.Ebook = await this.$_ebookDataFetch_getEbook(
        location,
        requestPayload
      )

      this.isEbookLoaded = true
    }
  },
  mounted() {
    const dataKeysForDelayedContentRefresh = Object.keys(
      generateDefaultArticleContentDataModel()
    )

    this.$_delayedContentRefresh_run({
      getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
      requestNewDataFn: async () => {
        const newData = await getArticlePageData({
          route: this.$route,
          store: this.$store
        })

        return pick(dataKeysForDelayedContentRefresh, newData)
      },
      dataComparisonKeysToOmitDeep: ['ModifiedOn'],
      dataChangedCallbackFn: newData => {
        Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
        this.$bus.$emit(RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME)
      }
    })
  }
}
</script>
